// src/components/layout/ChatNavItem.jsx
// Ítem de navegación "Chat" con badge de no-leídos total.
// Recalcula total al cambiar de ruta y escucha socket para incrementar en vivo.
// Además dispara notificación del navegador y un beep cuando llega un mensaje
// y el usuario no está actualmente dentro de la ruta /chat.
import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import socket from '../../services/socket';
import { listMyChats } from '../../services/chatApi';
import { notify, requestNotifyPermission } from '../../utils/notify';

// Beep corto generado con WebAudio — evita depender de un asset externo.
function playBeep() {
    try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        const ctx = new Ctx();
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = 'sine';
        o.frequency.value = 880;
        g.gain.value = 0.04;
        o.connect(g); g.connect(ctx.destination);
        o.start();
        setTimeout(() => { o.stop(); ctx.close(); }, 120);
    } catch { /* silent */ }
}

export default function ChatNavItem() {
    const { user }       = useContext(AuthContext);
    const location       = useLocation();
    const [total, setTotal] = useState(0);
    const locationRef = useRef(location.pathname);
    useEffect(() => { locationRef.current = location.pathname; }, [location.pathname]);

    useEffect(() => {
        if (user) requestNotifyPermission();
    }, [user]);

    useEffect(() => {
        let alive = true;
        listMyChats()
            .then(chats => {
                if (!alive) return;
                setTotal((chats || []).reduce((n, c) => n + (c.unreadCount || 0), 0));
            })
            .catch(() => { if (alive) setTotal(0); });
        return () => { alive = false; };
    }, [location.pathname]);

    useEffect(() => {
        const onMessage = ({ message }) => {
            if (String(message.sender?._id || message.sender) === String(user?.id)) return;
            const inChatRoute = locationRef.current === '/chat';
            if (!inChatRoute) {
                const fromName = message.sender?.name || 'Alguien';
                const preview  = message.content
                    || (message.attachments?.length ? '📎 Adjunto' : '');
                notify(`Nuevo mensaje de ${fromName}`, preview);
                playBeep();
                setTotal(t => t + 1);
            }
        };
        socket.on('messageCreated', onMessage);
        return () => socket.off('messageCreated', onMessage);
    }, [user?.id]);

    useEffect(() => {
        const onRead = ({ userId }) => {
            if (String(userId) !== String(user?.id)) return;
            listMyChats()
                .then(chats => setTotal((chats || []).reduce((n, c) => n + (c.unreadCount || 0), 0)))
                .catch(() => {});
        };
        socket.on('messageRead', onRead);
        return () => socket.off('messageRead', onRead);
    }, [user?.id]);

    const active = location.pathname === '/chat' || location.pathname.startsWith('/chat/');

    return (
        <Link to="/chat" className={`tb-link${active ? ' active' : ''}`}>
            Chat
            {total > 0 && (
                <span className="tb-badge">{total > 99 ? '99+' : total}</span>
            )}
        </Link>
    );
}
